import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '@/context/AppContext';
import { STYLE_OPTIONS } from '@/utils/constants';
import { StyleOption } from '@/types/common';
import styles from '@/styles/components/StyleSelectionStep.module.css';

export default function StyleSelectionStep() {
  const { t, i18n } = useTranslation();
  const { state, dispatch } = useApp();

  const localizedStyles = useMemo((): StyleOption[] => {
    return STYLE_OPTIONS.map(style => ({
      key: style.key,
      name: i18n.language === 'zh' ? style.name : style.key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
      img: style.img,
    }));
  }, [i18n.language]);

  const handleStyleSelect = useCallback((styleKey: string) => {
    dispatch({
      type: 'SET_PREFERENCES',
      payload: {
        ...state.preferences,
        style: styleKey,
      },
    });
  }, [dispatch, state.preferences]);

  const handleCustomTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch({
      type: 'SET_PREFERENCES',
      payload: {
        ...state.preferences,
        customText: e.target.value,
      },
    });
  }, [dispatch, state.preferences]);

  const handleBack = useCallback(() => {
    dispatch({ type: 'SET_STEP', payload: 1 });
  }, [dispatch]);

  const handleNext = useCallback(() => {
    dispatch({ type: 'SET_STEP', payload: 3 });
  }, [dispatch]);

  return (
    <div className="step-content">
      <h2 className="step-title">{t('step2Title')}</h2>
      <p className="text-center" style={{ marginBottom: 'var(--spacing-xl)', color: 'var(--color-border)' }}>
        {t('step2Subtitle')}
      </p>

      <div className={styles.styleGrid}>
        {localizedStyles.map((style) => (
          <div
            key={style.key}
            className={`${styles.styleCard} ${
              state.preferences.style === style.key ? styles.selected : ''
            }`}
            onClick={() => handleStyleSelect(style.key)}
          >
            <img src={style.img} alt={style.name} className={styles.styleImage} />
            <div className={styles.styleName}>{style.name}</div>
          </div>
        ))}
      </div>

      <div className={styles.customSection}>
        <h3 className={styles.customTitle}>{t('step2Custom')}</h3>
        <textarea
          className={styles.customTextarea}
          placeholder={t('step2CustomPlaceholder')}
          value={state.preferences.customText}
          onChange={handleCustomTextChange}
        />
      </div>

      <div className="button-group" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <button className="button button-secondary" onClick={handleBack}>
          {t('buttonBack')}
        </button>
        <button
          className="button"
          onClick={handleNext}
          disabled={!state.preferences.style}
        >
          {t('buttonGenerate')}
        </button>
      </div>
    </div>
  );
}